import Stack from '@mui/material/Stack';

import { AppButton, WhatsAppButton } from '@/components/atoms';
import { type NavLink } from '@/config/navigation';

type Props = {
  /** Prefilled WhatsApp message, or null to omit the green button. */
  whatsappMessage: string | null;
  /** The advice CTA, or null to omit it. */
  consultation: NavLink | null;
};

/**
 * Narrow-phone sizing. The two labels have to sit side by side at 320px,
 * where the row has about 136px per button: the theme's 20px button padding
 * plus a 14px label overflows that, and MUI answers an overflow by wrapping
 * the label onto a second line, which makes the two buttons different heights.
 *
 * So the padding and type step down one notch below `sm` and back up above it,
 * and `nowrap` makes any future label that still does not fit fail visibly in
 * review rather than quietly growing the bar. `flex: 1` with `minWidth: 0` is
 * what keeps the pair exactly equal — and makes a lone survivor full width
 * without a second code path.
 */
const actionSx = {
  flex: 1,
  minWidth: 0,
  whiteSpace: 'nowrap',
  px: { xs: 1.5, sm: 2.5 },
  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
} as const;

/**
 * The bar's two actions: WhatsApp, then the free-advice CTA.
 *
 * Rank, not equality (IA §5): WhatsApp keeps its fill and the advice route
 * steps down to an outline, so the row has one primary. The outline is the
 * brand red rather than the ink, which is what makes it the same button a
 * visitor already met in the header and the drawer.
 *
 * Both colours are palette roles, so the pair follows the scheme into dark
 * mode with no second set of values to keep in step.
 *
 * Purely presentational — it is handed what to show and decides nothing. See
 * `useStickyActionBar` for which of these exist on a given route.
 */
export function StickyActionButtons({ whatsappMessage, consultation }: Props) {
  return (
    <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} sx={{ p: { xs: 1.5, sm: 2 } }}>
      {whatsappMessage !== null && (
        <WhatsAppButton message={whatsappMessage} sx={actionSx}>
          WhatsApp
        </WhatsAppButton>
      )}
      {consultation && (
        <AppButton to={consultation.to} appVariant="primaryOutline" sx={actionSx}>
          {consultation.label}
        </AppButton>
      )}
    </Stack>
  );
}
